import * as React from "react";

import {t} from "i18next";
import {Checkbox, Dimmer, Icon, Label, Loader, Pagination, Table} from "semantic-ui-react";

import Button from "../../../components/Button";
import {DerivateUtils} from "../../../components/Table/components/derivate/DerivateUtils";
import {ModeltypeBasicDto} from "../../../services/Client";
import DataAdapterOptionsView from "../DataAdapterOptionsView";
import {
    AMTB_OVERVIEW_PAGE_SIZE,
    IAMTBOverviewPage,
    isInitialWorkflowStep,
    isLastWorkflowStep,
    renderOptionTexts
} from "./AMTBTransferUtils";

export interface ISelectAllAMTBSummaryTableProps {
    sourceMTB: ModeltypeBasicDto;
    targetMTBs: ModeltypeBasicDto[];
    userLanguage: string;
    /** Options matching the current filter (before exclusions) – used until the first page reports the server total. */
    selectAllTotal: number;
    /** Rows the user unchecked (grid + this dialog). */
    excludedIds: number[];
    fetchOverviewPage: (page: number, size: number) => Promise<IAMTBOverviewPage>;

    onToggle: (objectId: number, checked: boolean) => void;
    backAction: () => void;
    nextAction: () => void;
}

interface ISelectAllAMTBSummaryTableState {
    page: number;
    items: DataAdapterOptionsView[];
    total: number;
    loading: boolean;
    error: boolean;
}

export class SelectAllAMTBSummaryTable extends React.Component<ISelectAllAMTBSummaryTableProps, ISelectAllAMTBSummaryTableState> {
    private unmounted = false;

    constructor(props: ISelectAllAMTBSummaryTableProps) {
        super(props);
        this.state = {
            page: 1,
            items: [],
            total: props.selectAllTotal || 0,
            loading: false,
            error: false
        };
    }

    componentDidMount(): void {
        this.loadPage(1);
    }

    componentWillUnmount(): void {
        this.unmounted = true;
    }

    componentDidUpdate(prevProps: Readonly<ISelectAllAMTBSummaryTableProps>): void {
        if (prevProps.fetchOverviewPage !== this.props.fetchOverviewPage) {
            this.loadPage(1);
        }
    }

    private loadPage = (page: number) => {
        this.setState({loading: true, error: false, page});
        this.props.fetchOverviewPage(page - 1, AMTB_OVERVIEW_PAGE_SIZE)
            .then((result: IAMTBOverviewPage) => {
                if (this.unmounted) {
                    return;
                }
                this.setState({
                    items: result && result.items ? result.items : [],
                    total: result && result.total !== undefined && result.total !== null ? result.total : this.state.total,
                    loading: false
                });
            })
            .catch(() => {
                if (this.unmounted) {
                    return;
                }
                this.setState({items: [], loading: false, error: true});
            });
    }

    private getSelectedCount = (): number =>
        Math.max(0, this.state.total - this.props.excludedIds.length)

    private renderWorkflowBadge = (item: DataAdapterOptionsView) =>
        isInitialWorkflowStep(item)
            ? <Label basic={true} color={'green'} size={'small'}>{t("AMTB_TRANSFER:INITIAL_WF_TAB")}</Label>
            : <Label basic={true} color={'orange'} size={'small'}>{t("AMTB_TRANSFER:WF_STARTED_TAB")}</Label>

    render() {
        const selectedCount = this.getSelectedCount();
        const totalPages = Math.max(1, Math.ceil(this.state.total / AMTB_OVERVIEW_PAGE_SIZE));
        const excluded = new Set<number>(this.props.excludedIds);

        return (
            <>
                <div style={{marginBottom: '20px'}}>
                    <p>
                        <b>{selectedCount}</b>
                        {' ' + t(selectedCount === 1 ? "AMTB_TRANSFER:ALL_ITEMS_COUNT_SINGLE" : "AMTB_TRANSFER:ALL_ITEMS_COUNT_MULTI")}
                    </p>
                    <p>
                        <Icon name="info circle" color="blue" />
                        {t("AMTB_TRANSFER:SELECT_ALL_SUMMARY_TXT", {
                            defaultValue: "All options matching the current filter are selected. Options in the first workflow step "
                                + "are transferred directly; for all other options the workflow is not reset and the target "
                                + "assignment is set as draft. Uncheck a row to exclude it from the transfer."
                        })}
                    </p>
                    {this.state.error &&
                        <p style={{color: 'red'}}>
                            <Icon name="exclamation triangle" color="red" />
                            {t("AMTB_TRANSFER:OVERVIEW_LOAD_ERROR", {
                                defaultValue: "The overview page could not be loaded. You can still continue – the transfer is "
                                    + "resolved on the server by the current filter."
                            })}
                        </p>
                    }
                </div>

                <div style={{position: 'relative'}} className={'scrollbarX'}>
                    {this.state.loading &&
                        <Dimmer active={true} inverted={true}>
                            <Loader />
                        </Dimmer>
                    }
                    <Table
                        celled={true}
                        structured={true}
                        textAlign={"center"}
                    >
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell rowSpan={2} />
                                <Table.HeaderCell rowSpan={2}>{t("AMTB_TRANSFER:OPTION_IDENTIFIER")}</Table.HeaderCell>
                                <Table.HeaderCell rowSpan={2}>{t("AMTB_TRANSFER:SHORTTEXT")}</Table.HeaderCell>
                                <Table.HeaderCell rowSpan={2}>{t("AMTB_TRANSFER:WORKFLOW_STEP", {defaultValue: "Workflow step"})}</Table.HeaderCell>
                                <Table.HeaderCell>{t("AMTB_TRANSFER:SOURCE_ASSIGNMENT")}</Table.HeaderCell>
                                <Table.HeaderCell colSpan={this.props.targetMTBs.length}>{t("AMTB_TRANSFER:TARGET_ASSIGNMENTS")}</Table.HeaderCell>
                                <Table.HeaderCell rowSpan={2}>{t("AMTB_TRANSFER:PREVIEW_ASSIGNMENTS")}</Table.HeaderCell>
                            </Table.Row>
                            <Table.Row>
                                <Table.HeaderCell>{DerivateUtils.renderKeyDerivate(this.props.sourceMTB, this.props.userLanguage)}</Table.HeaderCell>
                                {this.props.targetMTBs.map((mtb, index) =>
                                    <Table.HeaderCell key={'target-mtb-' + index}>
                                        {DerivateUtils.renderKeyDerivate(mtb, this.props.userLanguage)}
                                    </Table.HeaderCell>
                                )}
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {this.state.items.map((item: DataAdapterOptionsView, index: number) => (
                                <Table.Row key={'row-' + index}>
                                    <Table.Cell>
                                        <Checkbox
                                            slider={true}
                                            checked={!excluded.has(item.objectId)}
                                            onChange={(e, v) => this.props.onToggle(item.objectId, v.checked as boolean)}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Label size={'large'} className={'whitespace--nowrap'}>
                                            {item.optionBasicView}
                                            <Label.Detail>
                                                {item.optionTypeView}
                                            </Label.Detail>
                                        </Label>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {renderOptionTexts(item.texts)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {this.renderWorkflowBadge(item)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {DerivateUtils.getAMTBIcon(item.dto.modeltypes || [], this.props.sourceMTB)}
                                    </Table.Cell>
                                    {this.props.targetMTBs.map((mtb, idx) =>
                                        <Table.Cell key={'target-amtb-' + idx}>{DerivateUtils.getAMTBIcon(item.dto.modeltypes || [], mtb)}</Table.Cell>
                                    )}
                                    <Table.Cell>
                                        {DerivateUtils.getPreviewAMTBIcon(item.dto.modeltypes || [], this.props.sourceMTB, isLastWorkflowStep(item))}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>

                        <Table.Footer>
                            <Table.Row>
                                <Table.HeaderCell colSpan={6 + this.props.targetMTBs.length}>
                                    <Pagination
                                        activePage={this.state.page}
                                        totalPages={totalPages}
                                        onPageChange={(e, c) => this.loadPage(c.activePage as number)}
                                    />
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Footer>
                    </Table>
                </div>

                <div style={{marginTop: '30px'}}>
                    <Button
                        icon={'arrow left'}
                        onClick={this.props.backAction}
                    />
                    <div style={{float: 'right'}}>
                        {selectedCount < 1 &&
                            <Label color={'red'} pointing={'right'}>
                                <Icon name={'warning'} />
                                {t("AMTB_TRANSFER:EMPTY_LIST_HINT")}
                            </Label>}
                        <Button
                            icon={'arrow right'}
                            style={{float: 'right'}}
                            disabled={selectedCount < 1 || this.state.loading}
                            onClick={this.props.nextAction}
                        />
                    </div>
                </div>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/views/OptionsView/amtbTransfer/SelectAllAMTBSummaryTable.tsx